import { useMemo } from 'react';

import {
  BINARY_DECOMPOSITION,
  SNIPPET_DETECTION,
  VENDORED_DEPENDENCY_DETECTION,
} from '../../../../../shared/featureFlags/flagNames';
import { useIsFeatureEnabled } from '../../../../helpers/features/featureFlags';
import type { ProjectType } from '../../../../../typings/enums';
import type { EnhancedScanFeaturesAvailableResult } from '../types';

/**
 * A hook for figuring out which enhanced scan featurs are available, based on enbaled features and projectTypes.
 *
 *  * @param {Set<ProjectType>} projectTypes - The set of project types for this project or releaseGroup
 *
 */
export function useEnhancedScanFeaturesAvailable(projectTypes: Set<ProjectType>): EnhancedScanFeaturesAvailableResult {
  // Feature flag checks
  const isSnippetScanningEnabled = useIsFeatureEnabled(SNIPPET_DETECTION);
  const isBinaryDecompositionEnabled = useIsFeatureEnabled(BINARY_DECOMPOSITION);
  const isVendoredDependenciesEnabled = useIsFeatureEnabled(VENDORED_DEPENDENCY_DETECTION);

  const areAnyGatedFeaturesEnabled = (
    isSnippetScanningEnabled ||
    isVendoredDependenciesEnabled ||
    isBinaryDecompositionEnabled
  );
  const isInventoryUIFeatureEnabled = areAnyGatedFeaturesEnabled;

  const areSnippetsAvailable = (
    isSnippetScanningEnabled &&
    (projectTypes.has('autobuild') || projectTypes.has('provided'))
  );

  const areVendoredDependenciesAvailable = (
    isVendoredDependenciesEnabled &&
    (projectTypes.has('autobuild') || projectTypes.has('provided'))
  );

  const isBinaryAnalysisAvailable = isBinaryDecompositionEnabled && projectTypes.has('binary');

  return useMemo(() => ({
    areSnippetsAvailable,
    areVendoredDependenciesAvailable,
    isBinaryAnalysisAvailable,
    isInventoryUIFeatureEnabled,
  }), [
    areSnippetsAvailable,
    areVendoredDependenciesAvailable,
    isBinaryAnalysisAvailable,
    isInventoryUIFeatureEnabled,
  ]);
}
